import { useMemo } from 'react'
import {
  useQueryInventoryItems,
  useQueryInventoryMovements,
  useQueryInventoryRecipeByItem,
  useQueryInventoryRecipeComponents,
  useQueryInventoryStock,
  useQueryInventoryWarehouses
} from '../../../../../../queries/inventoryQueries'
import { getNextWarehouseCode } from '../utils/inventoryWarehouse'
import {
  getItemOptionLabel,
  resolveItemId,
  resolveItemType,
  resolveItemUnit,
  resolveRecipeId,
  resolveWarehouseId,
  toBoolean
} from '../utils/inventoryResolvers'

const useInventoryData = ({ businessId, state }) => {
  const itemParams = useMemo(() => ({ limit: 500 }), [])

  const stockParams = useMemo(
    () => ({
      searchQuery: state.stockSearch.trim() || undefined,
      limit: 200
    }),
    [state.stockSearch]
  )

  const movementParams = useMemo(
    () => ({
      searchQuery: state.movementSearch.trim() || undefined,
      type: state.movementType || undefined,
      limit: 200
    }),
    [state.movementSearch, state.movementType]
  )

  const warehousesQuery = useQueryInventoryWarehouses(businessId, { enabled: !!businessId })
  const itemsQuery = useQueryInventoryItems(businessId, itemParams, { enabled: !!businessId })
  const stockQuery = useQueryInventoryStock(businessId, stockParams, { enabled: !!businessId })
  const movementsQuery = useQueryInventoryMovements(businessId, movementParams, { enabled: !!businessId })
  const recipeByItemQuery = useQueryInventoryRecipeByItem(businessId, Number(state.selectedBomItemId || 0), {
    enabled: !!businessId && !!state.selectedBomItemId
  })

  const selectedRecipe = recipeByItemQuery.data
  const selectedRecipeId = resolveRecipeId(selectedRecipe)

  const recipeComponentsQuery = useQueryInventoryRecipeComponents(businessId, selectedRecipeId, {
    enabled: !!businessId && !!selectedRecipeId
  })

  const warehouses = warehousesQuery?.data?.rows || []
  const items = itemsQuery?.data?.rows || []
  const stock = stockQuery?.data?.rows || []
  const movements = movementsQuery?.data?.rows || []
  const recipeComponentsRows = recipeComponentsQuery?.data?.rows || []
  const normalizedItemSearch = state.itemSearch.trim().toLowerCase()

  const visibleWarehouses = useMemo(
    () =>
      warehouses.filter(row => toBoolean(row?.is_active ?? row?.isActive ?? row?.active ?? true)),
    [warehouses]
  )

  const nextWarehouseCode = useMemo(() => getNextWarehouseCode(warehouses), [warehouses])
  const hasWarehouseCapacity = !!nextWarehouseCode

  const visibleItems = useMemo(
    () =>
      items.filter(row => {
        const matchesType = state.itemTypeFilter ? resolveItemType(row) === state.itemTypeFilter : true
        const haystack = `${row?.sku ?? ''} ${row?.name ?? row?.nombre ?? ''}`.toLowerCase()
        const matchesSearch = normalizedItemSearch ? haystack.includes(normalizedItemSearch) : true
        return matchesType && matchesSearch
      }),
    [items, normalizedItemSearch, state.itemTypeFilter]
  )

  const itemOptions = useMemo(
    () =>
      items
        .map(row => ({
          id: resolveItemId(row),
          sku: row?.sku ?? '',
          name: row?.name ?? row?.nombre ?? 'Sin nombre',
          unit: resolveItemUnit(row),
          price:
            row?.price === null || row?.price === undefined
              ? row?.precio === null || row?.precio === undefined
                ? ''
                : String(row?.precio)
              : String(row?.price),
          itemType: String(resolveItemType(row) || '').toUpperCase(),
          controlsInventory: toBoolean(
            row?.controls_inventory ?? row?.controlsInventory ?? row?.controla_inventario ?? false
          ),
          label: getItemOptionLabel(row)
        }))
        .filter(option => option.id > 0),
    [items]
  )

  const stockOperationItemOptions = useMemo(
    () => itemOptions.filter(option => option.itemType === 'PRODUCT' && option.controlsInventory),
    [itemOptions]
  )

  const warehouseOptions = useMemo(
    () =>
      visibleWarehouses
        .map(row => ({
          id: resolveWarehouseId(row),
          code: row?.code ?? row?.codigo ?? '',
          name: row?.name ?? row?.nombre ?? 'Sin nombre',
          label: `${row?.code ?? row?.codigo ?? 'SIN-COD'} · ${row?.name ?? row?.nombre ?? 'Sin nombre'}`
        }))
        .filter(option => option.id > 0),
    [visibleWarehouses]
  )

  const activeWarehouseOptions = warehouseOptions

  const bomItemOptions = useMemo(
    () => itemOptions.filter(option => option.itemType === 'BOM_PRODUCT'),
    [itemOptions]
  )

  const recipeComponentOptions = useMemo(
    () => itemOptions.filter(option => option.id !== Number(state.selectedBomItemId || 0)),
    [itemOptions, state.selectedBomItemId]
  )

  const isLoadingAny =
    warehousesQuery.isLoading || itemsQuery.isLoading || stockQuery.isLoading || movementsQuery.isLoading

  const hubStats = [
    {
      label: 'Bodegas',
      value: warehousesQuery.isLoading ? '...' : String(visibleWarehouses.length)
    },
    {
      label: 'Catálogo',
      value: itemsQuery.isLoading ? '...' : String(items.length)
    },
    {
      label: 'Inventario visible',
      value: stockQuery.isLoading ? '...' : String(stock.length)
    },
    {
      label: 'Ajustes',
      value: movementsQuery.isLoading ? '...' : String(movements.length)
    }
  ]

  const hubCardEyebrows = {
    warehouses: warehousesQuery.isLoading ? 'Cargando bodegas' : `${visibleWarehouses.length} registradas`,
    items: itemsQuery.isLoading ? 'Cargando catálogo' : `${items.length} en catálogo`,
    operations: warehousesQuery.isLoading ? 'Cargando bodegas' : `${activeWarehouseOptions.length} bodegas activas`,
    recipes: itemsQuery.isLoading ? 'Cargando BOM' : `${bomItemOptions.length} items BOM`,
    salesNote: 'Documento interno',
    uploads: 'Items y stock inicial',
    stock: stockQuery.isLoading ? 'Cargando stock' : `${stock.length} filas visibles`,
    movements: movementsQuery.isLoading ? 'Cargando movimientos' : `${movements.length} registros recientes`,
    reports: 'Exploración bloqueada'
  }

  return {
    itemParams,
    stockParams,
    movementParams,
    warehousesQuery,
    itemsQuery,
    stockQuery,
    movementsQuery,
    recipeByItemQuery,
    recipeComponentsQuery,
    selectedRecipe,
    selectedRecipeId,
    warehouses,
    items,
    stock,
    movements,
    recipeComponentsRows,
    visibleWarehouses,
    nextWarehouseCode,
    hasWarehouseCapacity,
    visibleItems,
    itemOptions,
    stockOperationItemOptions,
    warehouseOptions,
    activeWarehouseOptions,
    bomItemOptions,
    recipeComponentOptions,
    isLoadingAny,
    hubStats,
    hubCardEyebrows
  }
}

export default useInventoryData
